import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Entity, EntityConnection } from '../../services/apiService';
import type { PortfolioSite, SiteMetadata } from '../api/portfolioApi';
import type { PortfolioComponentData, SelectedSiteData } from '../../services/portfolioComponentService';

// Extended site data with metadata (used by map tab)
export interface SiteDataWithMetadata extends SelectedSiteData {
  metadata?: SiteMetadata | null;
  geocodedCoordinates?: { lat: number; lng: number } | null;
}

interface PortfolioSiteSelectionState {
  // Selected site IDs (stored as array for Redux serialization)
  selectedSiteIds: string[];
  // Loaded site data with entities and metadata
  sitesData: SiteDataWithMetadata[];
  // Aggregated portfolio component data
  portfolioData: PortfolioComponentData | null;
  // Loading state
  isLoading: boolean;
  // Loading progress
  loadingProgress: {
    current: number;
    total: number;
  };
  // Whether initial data has been loaded
  hasLoadedData: boolean;

  // --- Overview map cache (persists across tab navigations) ---
  overviewSitesData: SiteDataWithMetadata[];
  overviewPortfolioData: PortfolioComponentData | null;
  // Comma-joined site IDs that were loaded — used to check cache validity
  overviewLoadedKey: string;
  overviewIsLoading: boolean;
}

const initialState: PortfolioSiteSelectionState = {
  selectedSiteIds: [],
  sitesData: [],
  portfolioData: null,
  isLoading: false,
  loadingProgress: { current: 0, total: 0 },
  hasLoadedData: false,
  overviewSitesData: [],
  overviewPortfolioData: null,
  overviewLoadedKey: '',
  overviewIsLoading: false,
};

const portfolioSiteSelectionSlice = createSlice({
  name: 'portfolioSiteSelection',
  initialState,
  reducers: {
    // Toggle a single site selection
    toggleSiteSelection: (state, action: PayloadAction<string>) => {
      const siteId = action.payload;
      const index = state.selectedSiteIds.indexOf(siteId);
      if (index >= 0) {
        state.selectedSiteIds.splice(index, 1);
      } else {
        state.selectedSiteIds.push(siteId);
      }
    },

    // Set all selected site IDs at once
    setSelectedSiteIds: (state, action: PayloadAction<string[]>) => {
      state.selectedSiteIds = action.payload;
    },

    // Clear all selections
    clearSelectedSites: (state) => {
      state.selectedSiteIds = [];
    },

    // Set loading state
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Update loading progress
    setLoadingProgress: (state, action: PayloadAction<{ current: number; total: number }>) => {
      state.loadingProgress = action.payload;
    },

    // Set loaded sites data
    setSitesData: (state, action: PayloadAction<SiteDataWithMetadata[]>) => {
      state.sitesData = action.payload;
      state.hasLoadedData = action.payload.length > 0;
    },

    // Set aggregated portfolio data
    setPortfolioData: (state, action: PayloadAction<PortfolioComponentData | null>) => {
      state.portfolioData = action.payload;
    },

    // --- Overview map cache reducers ---
    setOverviewSitesData: (state, action: PayloadAction<SiteDataWithMetadata[]>) => {
      state.overviewSitesData = action.payload;
    },
    setOverviewPortfolioData: (state, action: PayloadAction<PortfolioComponentData | null>) => {
      state.overviewPortfolioData = action.payload;
    },
    setOverviewLoadedKey: (state, action: PayloadAction<string>) => {
      state.overviewLoadedKey = action.payload;
    },
    setOverviewIsLoading: (state, action: PayloadAction<boolean>) => {
      state.overviewIsLoading = action.payload;
    },

    // Reset the entire state (e.g., when switching portfolios)
    resetPortfolioSiteSelection: () => initialState,
  },
});

export const {
  toggleSiteSelection,
  setSelectedSiteIds,
  clearSelectedSites,
  setIsLoading,
  setLoadingProgress,
  setSitesData,
  setPortfolioData,
  setOverviewSitesData,
  setOverviewPortfolioData,
  setOverviewLoadedKey,
  setOverviewIsLoading,
  resetPortfolioSiteSelection,
} = portfolioSiteSelectionSlice.actions;

export default portfolioSiteSelectionSlice.reducer;
